import { Injectable, signal } from '@angular/core';

export type Theme = 'light' | 'dark' | 'ocean' | 'forest' | 'crimson';

export interface ThemeDefinition {
  id: Theme;
  label: string;
  /** Bootstrap color mode the theme builds on (drives data-bs-theme). */
  base: 'light' | 'dark';
  /** Bootstrap icon shown in the theme picker. */
  icon: string;
  /** Representative accent color for the picker swatch. */
  swatch: string;
}

/**
 * The fixed theme catalogue. Themes are not user-customizable: each one is a
 * named token set defined in styles.scss under [data-pk-theme="..."].
 */
export const THEMES: readonly ThemeDefinition[] = [
  { id: 'light', label: 'Light', base: 'light', icon: 'bi-sun-fill', swatch: '#0d6efd' },
  { id: 'dark', label: 'Dark', base: 'dark', icon: 'bi-moon-stars-fill', swatch: '#6c757d' },
  { id: 'ocean', label: 'Ocean', base: 'light', icon: 'bi-water', swatch: '#0e7490' },
  { id: 'forest', label: 'Forest', base: 'light', icon: 'bi-tree-fill', swatch: '#2f7d4f' },
  { id: 'crimson', label: 'Crimson', base: 'light', icon: 'bi-fire', swatch: '#dc3545' }
];

const THEME_KEY = 'pk_theme';

@Injectable({ providedIn: 'root' })
export class ThemeService {
  readonly themes = THEMES;
  readonly theme = signal<Theme>('light');

  init(): void {
    const stored = localStorage.getItem(THEME_KEY);
    if (stored !== null && this.isTheme(stored)) {
      this.set(stored);
      return;
    }
    const prefersDark = window.matchMedia?.('(prefers-color-scheme: dark)').matches;
    this.set(prefersDark ? 'dark' : 'light');
  }

  set(theme: Theme): void {
    this.theme.set(theme);
    const def = THEMES.find((t) => t.id === theme) ?? THEMES[0];
    // data-bs-theme drives Bootstrap's built-in light/dark component styles;
    // data-pk-theme selects the PassKing token set layered on top.
    document.documentElement.setAttribute('data-bs-theme', def.base);
    document.documentElement.setAttribute('data-pk-theme', def.id);
    localStorage.setItem(THEME_KEY, theme);
  }

  definition(theme: Theme = this.theme()): ThemeDefinition {
    return THEMES.find((t) => t.id === theme) ?? THEMES[0];
  }

  private isTheme(value: string): value is Theme {
    return THEMES.some((t) => t.id === value);
  }
}
